import React from 'react';
    import { useBrandKit } from '../../contexts/BrandKitContext';
    import SafeIcon from '../../common/SafeIcon';
    import { assetsApi } from '../../lib/assetsApi';
    import * as FiIcons from 'react-icons/fi';
    import { format, isValid } from 'date-fns';

    const { FiLock, FiStar, FiCheck, FiImage, FiDroplet } = FiIcons;

    export default function BrandKitSummaryCard({ brandKit }) {
      const { currentBrandKit, setCurrentBrandKit } = useBrandKit();
      const [logoUrl, setLogoUrl] = React.useState(null);
      const [watermarkUrl, setWatermarkUrl] = React.useState(null);
      const [loadingAssets, setLoadingAssets] = React.useState(false);
      const isActive = currentBrandKit?.id === brandKit.id;

      const handleSetActive = (e) => {
        e.preventDefault();
        setCurrentBrandKit(brandKit);
      };

      React.useEffect(() => {
        const loadAssetThumbnails = async () => {
          setLoadingAssets(true);
          try {
            const logos = brandKit.logos || [];
            const watermarks = brandKit.watermarks || [];

            let logoToLoad = null;
            if (brandKit.selected_logo_id) {
              logoToLoad = logos.find(logo => logo.id === brandKit.selected_logo_id);
            }
            if (!logoToLoad && logos.length > 0) {
              logoToLoad = logos[0];
            }
            if (logoToLoad?.dropbox_path) {
              const url = await assetsApi.getAssetUrl(logoToLoad.dropbox_path);
              setLogoUrl(url);
            } else {
              setLogoUrl(null);
            }

            let watermarkToLoad = null;
            if (brandKit.selected_watermark_id) {
              watermarkToLoad = watermarks.find(watermark => watermark.id === brandKit.selected_watermark_id);
            }
            if (!watermarkToLoad && watermarks.length > 0) {
              watermarkToLoad = watermarks[0];
            }
            if (watermarkToLoad?.dropbox_path) {
              const url = await assetsApi.getAssetUrl(watermarkToLoad.dropbox_path);
              setWatermarkUrl(url);
            } else {
              setWatermarkUrl(null);
            }
          } catch (error) {
            console.warn('Failed to load asset thumbnails:', error);
          } finally {
            setLoadingAssets(false);
          }
        };
        loadAssetThumbnails();
      }, [brandKit]);

      const colors = Object.values(brandKit.colors || {});
      while (colors.length < 4) {
        colors.push('#E5E7EB');
      }

      const logos = brandKit.logos || [];
      const watermarks = brandKit.watermarks || [];

      return (
        <div className={`rounded-lg border-2 p-4 transition-all ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="font-semibold text-gray-800">{brandKit.name}</h3>
            <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-400" />
            {isActive && (
              <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full"> Active </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="relative">
              {loadingAssets ? (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center"> <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div> </div>
              ) : logoUrl ? (
                <img src={logoUrl} alt={`${brandKit.name} logo`} className="w-full h-24 object-contain bg-gray-50 rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : logos.length > 0 ? (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center"> <SafeIcon icon={FiImage} className="w-6 h-6 text-gray-400" /> </div>
              ) : (
                <div className="w-full h-24 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"> <SafeIcon icon={FiImage} className="w-6 h-6 text-gray-300" /> </div>
              )}
              <div className="absolute top-1 left-1 bg-white px-1 py-0.5 rounded text-xs font-medium text-gray-600 shadow-sm"> Logo {brandKit.selected_logo_id && '✓'} </div>
            </div>

            <div className="relative">
              {loadingAssets ? (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center"> <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div> </div>
              ) : watermarkUrl ? (
                <img src={watermarkUrl} alt={`${brandKit.name} watermark`} className="w-full h-24 object-contain bg-gray-50 rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : watermarks.length > 0 ? (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center"> <SafeIcon icon={FiDroplet} className="w-6 h-6 text-gray-400" /> </div>
              ) : (
                <div className="w-full h-24 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"> <SafeIcon icon={FiDroplet} className="w-6 h-6 text-gray-300" /> </div>
              )}
              <div className="absolute top-1 left-1 bg-white px-1 py-0.5 rounded text-xs font-medium text-gray-600 shadow-sm"> Watermark {brandKit.selected_watermark_id && '✓'} </div>
            </div>
          </div>

          {(() => {
            const raw = brandKit?.updated_at;
            const d = raw ? new Date(raw) : null;
            const label = d && isValid(d) ? format(d, 'P,p') : 'Never';
            return (
              <p className="text-xs text-gray-500 mb-3"> Modified: {label} </p>
            );
          })()}

          <div className="flex space-x-1 mb-3">
            {colors.slice(0, 4).map((color, index) => (
              <div key={index} style={{ backgroundColor: color }} className="h-2 flex-1 rounded-full" />
            ))}
          </div>

          <div className="text-xs text-gray-600 flex justify-between mb-4">
            <span>Logos: {logos.length}</span>
            <span>Watermarks: {watermarks.length}</span>
          </div>

          {isActive ? (
            <button disabled className="w-full py-2 text-sm font-semibold text-green-800 bg-green-200 rounded-md flex items-center justify-center">
              <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" /> Active Kit
            </button>
          ) : (
            <button onClick={handleSetActive} className="w-full py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"> Set Active </button>
          )}
        </div>
      );
    }